import React from 'react';
import { Box } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import PublicIcon from '@mui/icons-material/Public';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import MapIcon from '@mui/icons-material/Map';
import MailIcon from '@mui/icons-material/Mail';
import SettingsIcon from '@mui/icons-material/Settings';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VideocamIcon from '@mui/icons-material/Videocam';
import CloudIcon from '@mui/icons-material/Cloud';
import CallIcon from '@mui/icons-material/Call';
import SmsIcon from '@mui/icons-material/Sms';
import LanguageIcon from '@mui/icons-material/Language';
import PhoneFrame from '../_shared/PhoneFrame';
import { COLORS, MOCK } from '../_shared/tokens';

// Desaturated filler apps so the store app stands out on the springboard.
const FILLER = [
    { Icon: PhotoCameraIcon, bg: '#3a3f4a' },
    { Icon: ChatBubbleIcon, bg: '#2f7d4f' },
    { Icon: PublicIcon, bg: '#2b6fb0' },
    { Icon: CalendarTodayIcon, bg: '#c94b4b' },
    { Icon: MusicNoteIcon, bg: '#c96b2b' },
    { Icon: MapIcon, bg: '#4a8f6a' },
    { Icon: MailIcon, bg: '#3b78c2' },
    { Icon: SettingsIcon, bg: '#6b6f78' },
    { Icon: PhotoLibraryIcon, bg: '#c9a13b' },
    { Icon: VideocamIcon, bg: '#4a4f5a' },
    { Icon: CloudIcon, bg: '#5aa6c9' }
];

const DOCK = [
    { Icon: CallIcon, bg: '#2f9e4f' },
    { Icon: SmsIcon, bg: '#39a85a' },
    { Icon: PhotoCameraIcon, bg: '#3a3f4a' },
    { Icon: LanguageIcon, bg: '#2b6fb0' }
];

function AppIcon({ Icon, bg, size = 58 }) {
    return (
        <Box
            sx={{
                width: size,
                height: size,
                borderRadius: '14px',
                backgroundColor: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
            }}
        >
            <Icon sx={{ fontSize: size * 0.5, color: '#fff' }} />
        </Box>
    );
}

/** iOS springboard with the store app freshly installed and highlighted. */
export default function HomeScreenWithApp({ scale = 0.9, storeName, logoUrl }) {
    return (
        <PhoneFrame scale={scale}>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(165deg, #dfeee4 0%, #cfe4d6 45%, #bcd8c8 100%)'
                }}
            >
                <Box
                    sx={{
                        px: 2.75,
                        pt: 2.25,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        rowGap: 2.5,
                        columnGap: 2,
                        justifyItems: 'center'
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4 }}>

                        {logoUrl ? (
                            <Box
                                component="img"
                                alt="logo"
                                src={logoUrl}
                                sx={{
                                    height: 58,
                                    maxWidth: 58,
                                    objectFit: 'cover',
                                    borderRadius: '14px',
                                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
                                }}
                            />
                        ) : (
                            <Box
                                sx={{
                                    width: 58,
                                    height: 58,
                                    borderRadius: '14px',
                                    background: `linear-gradient(150deg, ${COLORS.green} 0%, ${COLORS.greenDark} 100%)`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: '0 0 0 3px rgba(60,213,104,0.6), 0 10px 26px rgba(60,213,104,0.4)',
                                    fontWeight: 700,
                                    fontSize: 26,
                                    color: '#fff'
                                }}
                            >
                                {storeName[0]}
                            </Box>
                        )}

                        <Box
                            sx={{
                                fontWeight: 600,
                                fontSize: 11,
                                color: '#12261a',
                                maxWidth: 66,
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {storeName}
                        </Box>
                    </Box>

                    {FILLER.map(({ Icon, bg }, i) => (
                        <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75 }}>
                            <AppIcon Icon={Icon} bg={bg} />
                            <Box sx={{ width: 66, height: 11, borderRadius: '3px', backgroundColor: 'rgba(18,38,26,0.14)' }} />
                        </Box>
                    ))}
                </Box>

                <Box
                    sx={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 14,
                        height: 82,
                        borderRadius: '26px',
                        backgroundColor: 'rgba(255,255,255,0.35)',
                        backdropFilter: 'blur(12px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-around',
                        px: 1.75
                    }}
                >
                    {DOCK.map(({ Icon, bg }, i) => (
                        <AppIcon key={i} Icon={Icon} bg={bg} />
                    ))}
                </Box>
            </Box>
        </PhoneFrame>
    );
}
